import React, { useState } from "react";
import { useDropzone } from "react-dropzone";
import Papa from "papaparse";
import * as XLSX from "xlsx";
import { Button } from "../ui/button";
import { Card } from "../ui/card";
import { Label } from "../ui/label";
import { useMutation } from "@apollo/client";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import { Badge } from "../ui/badge";
import Modal from "../ui/modal";
import { useModal } from "../../hooks/useModal";
import { BULK_IMPORT_EMPLOYEES } from "../../graphql/queries";

interface CsvRow {
  [key: string]: string;
}

interface ColumnMapping {
  [csvColumn: string]: string;
}

const FIELD_MAPPINGS = {
  email: { label: "Email", required: true },
  login: { label: "Login", required: true },
  firstName: { label: "Imię", required: true },
  lastName: { label: "Nazwisko", required: true },
  pin: { label: "PIN", required: false },
  role: { label: "Rola", required: false },
  department: { label: "Dział", required: false },
  positionId: { label: "Stanowisko ID", required: false },
  isActive: { label: "Aktywny", required: false },
  vacationDays: { label: "Dni urlopu", required: false },
  hireDate: { label: "Data zatrudnienia", required: false },
};

const EmployeeImport: React.FC = () => {
  const [csvData, setCsvData] = useState<CsvRow[]>([]);
  const [csvColumns, setCsvColumns] = useState<string[]>([]);
  const [columnMapping, setColumnMapping] = useState<ColumnMapping>({});
  const [importResult, setImportResult] = useState<any>(null);
  const [step, setStep] = useState<
    "upload" | "mapping" | "preview" | "results"
  >("upload");

  // Modal hook
  const {
    showErrorModal,
    showConfirmModal,
    modalData,
    showError,
    showConfirm,
    closeModal,
    confirmAction
  } = useModal();

  const [bulkImportEmployees, { loading: importing }] = useMutation(
    BULK_IMPORT_EMPLOYEES
  );

  const onDrop = (acceptedFiles: File[]) => {
    const file = acceptedFiles[0];
    if (file) {
      const fileType = file.type;
      const fileName = file.name.toLowerCase();

      // Check if it's an Excel file
      if (
        fileName.endsWith(".xlsx") ||
        fileName.endsWith(".xls") ||
        fileType ===
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
        fileType === "application/vnd.ms-excel"
      ) {
        // Handle Excel files
        const reader = new FileReader();
        reader.onload = (e) => {
          try {
            const data = new Uint8Array(e.target?.result as ArrayBuffer);
            const workbook = XLSX.read(data, { type: "array" });

            // Get the first worksheet
            const sheetName = workbook.SheetNames[0];
            const worksheet = workbook.Sheets[sheetName];

            // Convert to JSON with header row
            const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

            if (jsonData.length > 0) {
              // First row as headers
              const headers = jsonData[0] as string[];

              // Convert to object format like CSV
              const convertedData = jsonData
                .slice(1)
                .map((row: unknown) => {
                  const rowArray = row as any[];
                  const obj: CsvRow = {};
                  headers.forEach((header, index) => {
                    obj[header] = rowArray[index]
                      ? String(rowArray[index]).trim()
                      : "";
                  });
                  return obj;
                })
                .filter((row) =>
                  Object.values(row).some((value) => value.trim() !== "")
                );

              setCsvData(convertedData);
              setCsvColumns(headers);
              setStep("mapping");
            }
          } catch (error) {
            console.error("Error parsing Excel file:", error);
            showError(
              "Błąd odczytu pliku",
              "Błąd podczas odczytywania pliku Excel. Sprawdź czy plik nie jest uszkodzony."
            );
          }
        };
        reader.readAsArrayBuffer(file);
      } else {
        // Handle CSV files with PapaParse
        Papa.parse(file, {
          complete: (results: any) => {
            const data = results.data as CsvRow[];
            const filteredData = data.filter((row) =>
              Object.values(row).some((value) => value.trim() !== "")
            );

            setCsvData(filteredData);
            setCsvColumns(Object.keys(filteredData[0] || {}));
            setStep("mapping");
          },
          header: true,
          skipEmptyLines: true,
          encoding: "UTF-8",
        });
      }
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      "text/csv": [".csv"],
      "application/vnd.ms-excel": [".xls"],
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [
        ".xlsx",
      ],
    },
    maxFiles: 1,
  });

  const handleColumnMappingChange = (
    csvColumn: string,
    systemField: string
  ) => {
    setColumnMapping((prev) => ({
      ...prev,
      [csvColumn]: systemField,
    }));
  };

  const validateMapping = () => {
    const requiredFields = Object.entries(FIELD_MAPPINGS)
      .filter(([_, config]) => config.required)
      .map(([field, _]) => field);

    const mappedFields = Object.values(columnMapping);

    for (const requiredField of requiredFields) {
      if (!mappedFields.includes(requiredField)) {
        return `Pole "${
          FIELD_MAPPINGS[requiredField as keyof typeof FIELD_MAPPINGS].label
        }" jest wymagane i musi być zmapowane`;
      }
    }
    return null;
  };

  const handleProceedToPreview = () => {
    const validationError = validateMapping();
    if (validationError) {
      showError("Błąd mapowania", validationError);
      return;
    }
    setStep("preview");
  };

  const transformDataForImport = () => {
    return csvData
      .map((row) => {
        const transformedRow: any = {};

        Object.entries(columnMapping).forEach(([csvColumn, systemField]) => {
          let value = row[csvColumn];

          // Transform values based on field type
          switch (systemField) {
            case "isActive":
              transformedRow[systemField] =
                value?.toLowerCase() === "true" || value === "1";
              break;
            case "vacationDays":
              transformedRow[systemField] = value ? parseInt(value) : 26;
              break;
            case "hireDate":
              transformedRow[systemField] = value
                ? new Date(value).toISOString()
                : undefined;
              break;
            case "role":
              transformedRow[systemField] = value?.toUpperCase() || "EMPLOYEE";
              break;
            default:
              transformedRow[systemField] = value;
          }
        });

        // Set defaults for required fields
        if (!transformedRow.role) transformedRow.role = "EMPLOYEE";
        if (transformedRow.isActive === undefined)
          transformedRow.isActive = true;
        if (!transformedRow.vacationDays) transformedRow.vacationDays = 26;

        return transformedRow;
      })
      .filter((row) => {
        // Filter out rows where all required fields are empty
        const hasRequiredFields =
          row.email &&
          row.email.trim() !== "" &&
          row.login &&
          row.login.trim() !== "" &&
          row.firstName &&
          row.firstName.trim() !== "" &&
          row.lastName &&
          row.lastName.trim() !== "";
        return hasRequiredFields;
      });
  };

  const performImport = async (transformedData: any[]) => {
    try {
      const result = await bulkImportEmployees({
        variables: {
          data: {
            employees: transformedData,
          },
        },
      });

      setImportResult(result.data.bulkImportEmployees);
      setStep("results");
    } catch (error) {
      console.error("Import error:", error);
      showError(
        "Błąd importu",
        "Wystąpił błąd podczas importu pracowników. Spróbuj ponownie."
      );
    }
  };

  const handleImport = async () => {
    const originalCount = csvData.length;
    const transformedData = transformDataForImport();
    const filteredCount = transformedData.length;

    if (filteredCount === 0) {
      showError(
        "Brak danych do importu",
        "Nie znaleziono żadnych prawidłowych wierszy do zaimportowania. Sprawdź czy wszystkie wymagane pola są wypełnione."
      );
      return;
    }

    if (filteredCount < originalCount) {
      const skippedCount = originalCount - filteredCount;
      showConfirm(
        "Potwierdzenie importu",
        `Zostanie zaimportowanych ${filteredCount} z ${originalCount} wierszy. ${skippedCount} wierszy zostanie pominięte z powodu niekompletnych danych. Czy chcesz kontynuować?`,
        () => performImport(transformedData)
      );
    } else {
      await performImport(transformedData);
    }
  };

  const handleStartOver = () => {
    setCsvData([]);
    setCsvColumns([]);
    setColumnMapping({});
    setImportResult(null);
    setStep("upload");
  };

  const renderUploadStep = () => (
    <Card className="p-6">
      <h3 className="text-xl font-semibold mb-4">Import pracowników</h3>

      <div
        {...getRootProps()}
        className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
          isDragActive
            ? "border-primary bg-primary/10"
            : "border-border hover:border-primary/50"
        }`}
      >
        <input {...getInputProps()} />
        {isDragActive ? (
          <p className="text-primary">Upuść plik tutaj...</p>
        ) : (
          <div>
            <p className="text-muted-foreground mb-2">
              Przeciągnij i upuść plik tutaj lub kliknij, aby wybrać
            </p>
            <p className="text-sm text-muted-foreground">
              Obsługiwane formaty: CSV (.csv), Excel (.xls, .xlsx)
            </p>
          </div>
        )}
      </div>

      <div className="mt-6">
        <h4 className="font-semibold mb-2">Wymagane pola:</h4>
        <div className="grid grid-cols-2 gap-2">
          {Object.entries(FIELD_MAPPINGS).map(([field, config]) => (
            <div key={field} className="flex items-center gap-2">
              <Badge variant={config.required ? "default" : "secondary"}>
                {config.label}
              </Badge>
              {config.required && <span className="text-red-500">*</span>}
            </div>
          ))}
        </div>
        <p className="text-sm text-muted-foreground mt-2">
          * - pola wymagane. Jeśli nie podasz PIN-u, system automatycznie go
          wygeneruje.
        </p>
      </div>
    </Card>
  );

  const renderMappingStep = () => (
    <Card className="p-6">
      <h3 className="text-xl font-semibold mb-4">Mapowanie kolumn</h3>
      <p className="text-muted-foreground mb-4">
        Przypisz kolumny z pliku do pól w systemie. Pola oznaczone gwiazdką (*)
        są wymagane.
      </p>

      <div className="space-y-4">
        {csvColumns.map((csvColumn) => (
          <div key={csvColumn} className="grid grid-cols-2 gap-4 items-center">
            <Label className="font-medium">{csvColumn}</Label>
            <select
              value={columnMapping[csvColumn] || ""}
              onChange={(e) =>
                handleColumnMappingChange(csvColumn, e.target.value)
              }
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <option value="">-- Wybierz pole --</option>
              {Object.entries(FIELD_MAPPINGS).map(([field, config]) => (
                <option key={field} value={field}>
                  {config.label} {config.required && "*"}
                </option>
              ))}
            </select>
          </div>
        ))}
      </div>

      <div className="flex gap-4 mt-6">
        <Button onClick={handleStartOver} variant="outline">
          Anuluj
        </Button>
        <Button onClick={handleProceedToPreview}>Dalej</Button>
      </div>
    </Card>
  );

  const renderPreviewStep = () => {
    const transformedData = transformDataForImport();

    return (
      <Card className="p-6">
        <h3 className="text-xl font-semibold mb-4">Podgląd importu</h3>
        <p className="text-muted-foreground mb-4">
          Sprawdź dane przed importem. Zostanie zaimportowanych{" "}
          {transformedData.length} pracowników.
        </p>

        <div className="max-h-96 overflow-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Email</TableHead>
                <TableHead>Login</TableHead>
                <TableHead>Imię</TableHead>
                <TableHead>Nazwisko</TableHead>
                <TableHead>PIN</TableHead>
                <TableHead>Rola</TableHead>
                <TableHead>Dział</TableHead>
                <TableHead>Aktywny</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {transformedData.slice(0, 10).map((row, index) => (
                <TableRow key={index}>
                  <TableCell>{row.email}</TableCell>
                  <TableCell>{row.login}</TableCell>
                  <TableCell>{row.firstName}</TableCell>
                  <TableCell>{row.lastName}</TableCell>
                  <TableCell>{row.pin || "Auto-generated"}</TableCell>
                  <TableCell>{row.role}</TableCell>
                  <TableCell>{row.department || "-"}</TableCell>
                  <TableCell>{row.isActive ? "Tak" : "Nie"}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {transformedData.length > 10 && (
            <p className="text-sm text-muted-foreground mt-2">
              Pokazano pierwsze 10 rekordów z {transformedData.length}
            </p>
          )}
        </div>

        <div className="flex gap-4 mt-6">
          <Button onClick={() => setStep("mapping")} variant="outline">
            Wstecz
          </Button>
          <Button onClick={handleImport} disabled={importing}>
            {importing ? "Importuję..." : "Importuj"}
          </Button>
        </div>
      </Card>
    );
  };

  const renderResultsStep = () => (
    <Card className="p-6">
      <h3 className="text-xl font-semibold mb-4">Wyniki importu</h3>

      <div className="grid grid-cols-3 gap-4 mb-6">
        <div className="text-center">
          <div className="text-2xl font-bold text-blue-600">
            {importResult?.totalProcessed || 0}
          </div>
          <div className="text-sm text-gray-600">Przetworzonych</div>
        </div>
        <div className="text-center">
          <div className="text-2xl font-bold text-green-600">
            {importResult?.successCount || 0}
          </div>
          <div className="text-sm text-gray-600">Pomyślnych</div>
        </div>
        <div className="text-center">
          <div className="text-2xl font-bold text-red-600">
            {importResult?.errorCount || 0}
          </div>
          <div className="text-sm text-gray-600">Błędów</div>
        </div>
      </div>

      {importResult?.errors && importResult.errors.length > 0 && (
        <div className="mb-6">
          <h4 className="font-semibold mb-2 text-red-600">Błędy:</h4>
          <div className="space-y-2">
            {importResult.errors.map((error: any, index: number) => (
              <div key={index} className="bg-red-50 p-3 rounded border border-red-200">
                <div className="font-medium text-red-800">{error.email}</div>
                <div className="text-sm text-red-600">{error.error}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {importResult?.successful && importResult.successful.length > 0 && (
        <div className="mb-6">
          <h4 className="font-semibold mb-2 text-green-600">
            Pomyślnie zaimportowani:
          </h4>
          <div className="max-h-64 overflow-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Email</TableHead>
                  <TableHead>Login</TableHead>
                  <TableHead>Imię</TableHead>
                  <TableHead>Nazwisko</TableHead>
                  <TableHead>PIN</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {importResult.successful.map((result: any, index: number) => (
                  <TableRow key={index}>
                    <TableCell>{result.employee.email}</TableCell>
                    <TableCell>{result.employee.login}</TableCell>
                    <TableCell>{result.employee.firstName}</TableCell>
                    <TableCell>{result.employee.lastName}</TableCell>
                    <TableCell>{result.employee.pin}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      )}

      <Button onClick={handleStartOver}>Importuj kolejny plik</Button>
    </Card>
  );

  return (
    <div>
      {step === "upload" && renderUploadStep()}
      {step === "mapping" && renderMappingStep()}
      {step === "preview" && renderPreviewStep()}
      {step === "results" && renderResultsStep()}

      {/* Modals */}
      <Modal
        open={showErrorModal}
        onOpenChange={closeModal}
        modalData={modalData}
      />
      <Modal
        open={showConfirmModal}
        onOpenChange={closeModal}
        modalData={modalData}
        onConfirm={confirmAction}
        isConfirmModal={true}
      />
    </div>
  );
};

export default EmployeeImport;
